import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import withMediaToken from "../../services/mediaUrl";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { onClientUpdated } from "../../services/clientEvents";
import StatusChip from "../StatusChip";

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
	},

	pendingTicket: {
		cursor: "unset",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	// Linha do nome: nome ocupa o espaço livre, horário fica fixo à direita.
	// Era `space-between` com wrap, o que fazia o horário pular de linha em
	// nome comprido.
	contactNameWrapper: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		minWidth: 0,
	},

	contactName: {
		flex: 1,
		minWidth: 0,
	},

	lastMessageTime: {
		flex: "none",
		whiteSpace: "nowrap",
	},

	closedBadge: {
		flex: "none",
		alignSelf: "center",
		marginRight: 16,
	},

	// Linha da prévia: mensagem encolhe e trunca; contador e chip de conexão
	// têm largura própria reservada, em vez de o chip flutuar por cima.
	secondaryRow: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.75),
		minWidth: 0,
		marginTop: 2,
	},

	contactLastMessage: {
		flex: 1,
		minWidth: 0,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
	},

	newMessagesCount: {
		flex: "none",
		minWidth: 20,
		height: 18,
		padding: "0 6px",
		borderRadius: 9,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: 11,
		fontWeight: 700,
		lineHeight: 1,
		color: "#fff",
		backgroundColor: green[600],
	},

	badgeStyle: {
		color: "white",
		backgroundColor: green[500],
	},

	acceptButton: {
		position: "absolute",
		left: "50%",
	},

	ticketQueueColor: {
		flex: "none",
		width: "8px",
		height: "100%",
		position: "absolute",
		top: "0%",
		left: "0%",
	},

	// Chip da conexão (nome do chip/sessão de WhatsApp).
	// Era `position: absolute; right: 5; bottom: 5` — flutuava por cima da
	// prévia da mensagem e do contador de não lidas, que é exatamente o
	// sobreposto que aparecia na lista. Agora participa do fluxo, com largura
	// reservada e truncamento próprio.
	userTag: {
		flex: "none",
		maxWidth: 90,
		overflow: "hidden",
		textOverflow: "ellipsis",
		whiteSpace: "nowrap",
		padding: "1px 6px",
		borderRadius: 6,
		fontSize: 11,
		fontWeight: 600,
		lineHeight: 1.5,
		color: theme.palette.text.secondary,
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(148,163,184,0.16)"
				: theme.palette.grey[100],
		border: `1px solid ${theme.palette.divider}`,
	},
	// Etiqueta de grupo: precisa se distinguir do chip da conexão sem competir
	// com as tags do cliente, que são coloridas por configuração do usuário.
	groupTag: {
		flex: "none",
		padding: "1px 6px",
		borderRadius: 6,
		fontSize: 11,
		fontWeight: 600,
		lineHeight: 1.5,
		marginLeft: 6,
		color: theme.palette.primary.main,
		backgroundColor:
			theme.palette.type === "dark"
				? "rgba(37,99,235,0.18)"
				: "rgba(37,99,235,0.10)",
		border: `1px solid ${theme.palette.primary.main}33`,
	},
	clientChips: {
		display: "flex",
		flexWrap: "wrap",
		gap: 4,
		alignItems: "center",
		marginTop: 4,
	},
}));

const TicketListItem = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	// O ticket carrega a marca; o contato serve de reserva para registros
	// antigos, gravados antes de `Ticket.isGroup` existir.
	const isGroup = Boolean(ticket?.isGroup || ticket?.contact?.isGroup);
	const { ticketId } = useParams();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	const [clientInfo, setClientInfo] = useState(ticket?.contact?.client || null);

	useEffect(() => {
		setClientInfo(ticket?.contact?.client || null);
	}, [ticket?.contact?.client]);

	useEffect(() => {
		const clientId = ticket?.contact?.clientId;
		if (!clientId) return undefined;
		return onClientUpdated((updated) => {
			if (updated && updated.id === clientId) {
				setClientInfo((prev) => ({
					...(prev || {}),
					...updated,
					clientStatus: updated.clientStatus || prev?.clientStatus,
					tags: updated.tags || prev?.tags,
				}));
			}
		});
	}, [ticket?.contact?.clientId]);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleAcepptTicket = async id => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
			});
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
		if (isMounted.current) {
			setLoading(false);
		}
		history.push(`/tickets/${id}`);
	};

	const handleSelectTicket = id => {
		history.push(`/tickets/${id}`);
	};

	return (
		<React.Fragment key={ticket.id}>
			<ListItem
				dense
				button
				onClick={e => {
					if (ticket.status === "pending") return;
					handleSelectTicket(ticket.id);
				}}
				selected={ticketId && +ticketId === ticket.id}
				className={clsx(classes.ticket, {
					[classes.pendingTicket]: ticket.status === "pending",
				})}
			>
				<Tooltip
					arrow
					placement="right"
					title={ticket.queue?.name || "Sem fila"}
				>
					<span
						style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
						className={classes.ticketQueueColor}
					></span>
				</Tooltip>
				<ListItemAvatar>
					<Avatar src={withMediaToken(ticket?.contact?.profilePicUrl)} />
				</ListItemAvatar>
				<ListItemText
					disableTypography
					primary={
						<span className={classes.contactNameWrapper}>
							<Typography
								noWrap
								component="span"
								variant="body2"
								color="textPrimary"
								className={classes.contactName}
							>
								{ticket.contact.name}
							</Typography>
							{isGroup && (
								<span className={classes.groupTag}>
									{i18n.t("ticketsList.groupLabel")}
								</span>
							)}
							{ticket.status === "closed" && (
								<Badge
									className={classes.closedBadge}
									badgeContent={"closed"}
									color="primary"
									overlap="rectangular"
								/>
							)}
							{ticket.lastMessage && (
								<Typography
									className={classes.lastMessageTime}
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
										<>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
									) : (
										<>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
									)}
								</Typography>
							)}
						</span>
					}
					secondary={
						<>
							<span className={classes.secondaryRow}>
								<Typography
									className={classes.contactLastMessage}
									noWrap
									component="span"
									variant="body2"
									color="textSecondary"
								>
									{ticket.lastMessage ? (
										<MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
									) : (
										<br />
									)}
								</Typography>

								{ticket.unreadMessages > 0 && (
									<span className={classes.newMessagesCount}>
										{ticket.unreadMessages > 99 ? "99+" : ticket.unreadMessages}
									</span>
								)}

								{ticket.whatsappId && ticket.whatsapp?.name && (
									<Tooltip
										title={`${i18n.t("ticketsList.connectionTitle")}: ${ticket.whatsapp.name}`}
									>
										<span className={classes.userTag}>
											{ticket.whatsapp.name}
										</span>
									</Tooltip>
								)}

								{ticket.emailAccountId && ticket.emailAccount?.name && (
									<Tooltip title={`E-mail: ${ticket.emailAccount.fromEmail || ticket.emailAccount.name}`}>
										<span className={classes.userTag}>
											✉ {ticket.emailAccount.name}
										</span>
									</Tooltip>
								)}
							</span>
							{(clientInfo?.clientStatus || (clientInfo?.tags && clientInfo.tags.length > 0)) && (
								<span className={classes.clientChips}>
									{clientInfo?.clientStatus && (
										<StatusChip
											label={clientInfo.clientStatus.name}
											color={clientInfo.clientStatus.color}
										/>
									)}
									{(clientInfo?.tags || []).map((tag) => (
										<StatusChip
											key={tag.id}
											label={tag.name}
											color={tag.color}
										/>
									))}
								</span>
							)}
						</>
					}
				/>
				{ticket.status === "pending" && (
					<ButtonWithSpinner
						color="primary"
						variant="contained"
						className={classes.acceptButton}
						size="small"
						loading={loading}
						onClick={e => handleAcepptTicket(ticket.id)}
					>
						{i18n.t("ticketsList.buttons.accept")}
					</ButtonWithSpinner>
				)}
			</ListItem>
			<Divider variant="inset" component="li" />
		</React.Fragment>
	);
};

export default TicketListItem;
